import SpinnerArea from "../../../../components/SpinnerArea";
import useService from "../../../../hooks/useService";
import workflowService from "../../../../services/workflow.service";
import LegalQuestionSection from "../../../workflow/LegalQuestions";
import { TaskTypeProps } from "./TaskTypeProps";
import tasksService from "../../../../services/tasks.service";
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import PlaceholderControl from "../../../../components/PlaceholderControl";
import { FormikProps, useFormik } from "formik";
import { TaskLegalDto } from "../../../../models/task.model";
import * as Yup from "yup";
import styles from "../Task.module.scss";
import { useContext, useState } from "react";
import MainContext from "../../../../context/mainContext";
import { useNavigate } from "react-router";
import { SaveSuccessDialog, CancelDialog } from "../TaskDialogs";
import FileUploader, { FileWrapper } from "../../../../components/FileUploader";
import DocumentView from "../../../../components/DocumentView";
import moment from "moment";
import { validateBeforeExecute } from "../../../../misc/utils.functions";

const LegalReview: React.FC<TaskTypeProps> = ({ taskHeader }) => {
  const { t } = useTranslation();
  const { service } = useContext(MainContext);
  const navigate = useNavigate();

  const [generalInfo, generalInfoLoading] = useService(() =>
    workflowService.generalInfo(taskHeader.ticketNumber)
  );
  const [teamMembers, loadingTeamMembers] = useService(() =>
    tasksService.teamMembers(taskHeader.id)
  );
  const [details, loadingDetails] = useService(() =>
    tasksService.legalDetails(taskHeader.id)
  );

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelDialogShow, cancelDialogShowSet] = useState(false);

  const validationSchema = Yup.object().shape({
    assignedTo: Yup.number()
      .nullable()
      .required(t("requiredField")!)
      .min(0, t("requiredField")!),
    comments: Yup.string().nullable().optional().max(256),
  });

  const [uploadDocument, setUploadDocument] = useState<File | null>();

  const [saveTitle, setSaveTitle] = useState<string>(
    taskHeader.assignedTo ? t("saveSuccess")! : t("assignSuccess")!
  );

  const [requiredData, setRequiredData] = useState<string | null>(null);

  const formik = useFormik<TaskLegalDto>({
    initialValues: {
      assignedTo: taskHeader.assignedTo,
      comments: details?.comments,
      taskId: taskHeader.id,
    },
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const handleSaveContinueCondition = (): boolean => {
    if (!uploadDocument && !formik.values.comments && !details?.attachment) {
      setRequiredData(t("someDataRequired"));
      return false;
    }

    setRequiredData(null);
    return true;
  };

  const assign = (values: TaskLegalDto) => {
    service(() => tasksService.assignTo(values.taskId, values.assignedTo!), {
      callback: () => {
        setSaveSuccess(true);
      },
      loader: setSaving,
    });
  };

  const sendComments = (values: TaskLegalDto, completeTask: boolean) => {
    if (completeTask) setSaveTitle(t("sendCommentsSuccess")!);
    else setSaveTitle(t("saveSuccess")!);

    service(
      () => tasksService.legal({ ...values, completeTask }, uploadDocument),
      {
        callback: () => {
          setSaveSuccess(true);
        },
        loader: setSaving,
      }
    );
  };

  const cancel = (formik: FormikProps<TaskLegalDto>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  return (
    <SpinnerArea
      loading={
        loadingDetails || generalInfoLoading || loadingTeamMembers || saving
      }
    >
      <form
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
        className={styles.legalReviewContent}
      >
        {taskHeader.assignedTo ? (
          <FormLabel>
              <Trans 
                i18nKey="taskAssignedLabel"
                components={{
                  bold: <strong />,
                }}
                values={{
                  dueDate: moment(taskHeader.deadline).format("MM/DD/YYYY"),
                }}
              />
          </FormLabel>
        ) : (
          <>
            <FormLabel>
              <Trans 
                i18nKey="taskAssignLabel"
                components={{
                  bold: <strong />,
                }}
                values={{
                  dueDate: moment(taskHeader.deadline).format("MM/DD/YYYY"),
                }}
              />
            </FormLabel>
            <FormControl fullWidth className={styles.assignedToFormControl}>
              <FormLabel id="assigned-label" className={styles.assignedTo}>
                {t("assignTo")}:
              </FormLabel>
              <Select
                id="assignedTo"
                name="assignedTo"
                className={`${styles.teamMembers} ${
                  formik.touched.assignedTo && formik.errors.assignedTo
                    ? "Mui-error"
                    : ""
                }`}
                fullWidth
                value={
                  formik.values.assignedTo
                    ? formik.values.assignedTo.toString()
                    : "0"
                }
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                displayEmpty
                renderValue={(value) =>
                  value !== "0" ? (
                    teamMembers?.find((x) => x.id === parseInt(value))
                      ?.displayName
                  ) : (
                    <PlaceholderControl text={t("select")} />
                  )
                }
              >
                <MenuItem
                  key={`team_members_${0}`}
                  value={0}
                  className={"selectPlaceholder"}
                >
                  {t("select")}
                </MenuItem>
                {teamMembers?.map((x) => (
                  <MenuItem key={`team_members_${x.id}`} value={x.id!}>
                    {x.displayName}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText className="Mui-error">
                {formik.touched.assignedTo && formik.errors.assignedTo}
              </FormHelperText>
            </FormControl>
          </>
        )}

        <LegalQuestionSection
          answers={generalInfo ? generalInfo.answers : []}
          isReadOnly={true}
          propertyName="answers"
        />
        <div className={styles.commentsFormSection}>
          {taskHeader.assignedTo && (
            <>
              <div className={styles.commentSection}>
                <FormControl fullWidth>
                  <FormLabel id="description-label">
                    {t("addAComment")}
                  </FormLabel>
                  <TextField
                    id="comments"
                    name="comments"
                    placeholder={t("comment")!}
                    fullWidth
                    rows={3}
                    multiline
                    value={formik.values.comments || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    helperText={
                      formik.touched.comments && formik.errors.comments
                    }
                    error={
                      formik.touched.comments && Boolean(formik.errors.comments)
                    }
                  />
                  {requiredData && (
                    <FormHelperText error>{requiredData}</FormHelperText>
                  )}
                </FormControl>

                <FileUploader
                  title="uploadLegalComments"
                  onChange={(file: File | File[] | FileWrapper[]) => {
                    setUploadDocument(file as File);
                  }}
                  allowedExtensions={[".doc", ".docx"]}
                  isReadOnly={false}
                />
              </div>
              <div>
                {details && details.attachment && (
                  <DocumentView document={details.attachment!} />
                )}
              </div>
            </>
          )}
        </div>

        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>
          {taskHeader.assignedTo ? (
            <>
              <Button
                className={styles.save}
                onClick={() =>
                  validateBeforeExecute(
                    formik,
                    () => sendComments(formik.values, false),
                    handleSaveContinueCondition
                  )
                }
              >
                {t("save")}
              </Button>
              <Button
                className={styles.sendComments}
                onClick={() =>
                  validateBeforeExecute(
                    formik,
                    () => sendComments(formik.values, true),
                    handleSaveContinueCondition
                  )
                }
              >
                {t("sendComments")}
              </Button>
            </>
          ) : (
            <Button
              className={styles.assignTo}
              onClick={() =>
                validateBeforeExecute(formik, () => assign(formik.values))
              }
            >
              {t("assign")}
            </Button>
          )}
        </div>
      </form>
      <CancelDialog
        open={cancelDialogShow}
        onAccept={() => navigate(-1)}
        onClose={() => cancelDialogShowSet(false)}
      />
      <SaveSuccessDialog
        open={saveSuccess}
        onAccept={() => {}}
        onClose={() => setSaveSuccess(false)}
        title={saveTitle}
      />
    </SpinnerArea>
  );
};

export default LegalReview;
